import { VideoEditor } from '../VideoEditor';
import { EditorChangeType, EditorObserver } from '../types';
import { LogManager } from '../../utility/Logger';

/**
 * ObserverManager is responsible for managing observer registrations, notifications,
 * and handling the notification queue for the VideoEditor.
 */
export class ObserverManager {
  private observers: Map<EditorChangeType, Set<EditorObserver>> = new Map();
  private pendingNotifications: Array<{ changeType: EditorChangeType; payload?: any }> = [];
  private logger = LogManager.getLogger('observer-manager');

  /**
   * Creates a new ObserverManager instance
   * @param editor The VideoEditor instance this manager coordinates with
   */
  constructor(private editor: VideoEditor) {
    // Initialize the observer sets for each change type
    Object.values(EditorChangeType).forEach((type) => {
      this.observers.set(type, new Set<EditorObserver>());
    });
  }

  /**
   * Registers an observer for specific change types
   * @param observer The observer to register
   * @param changeTypes The types of changes to observe (if none provided, observes all changes)
   */
  registerObserver(
    observer: EditorObserver,
    changeTypes?: EditorChangeType[],
  ): void {
    // If no specific change types are provided, register for all types
    const typesToRegister = changeTypes || Object.values(EditorChangeType);

    typesToRegister.forEach((type) => {
      const observersForType = this.observers.get(type);
      if (observersForType) {
        observersForType.add(observer);
      }
    });
  }

  /**
   * Unregisters an observer from specific change types
   * @param observer The observer to unregister
   * @param changeTypes The types of changes to unobserve (if none provided, unobserves all changes)
   */
  unregisterObserver(
    observer: EditorObserver,
    changeTypes?: EditorChangeType[],
  ): void {
    // If no specific change types are provided, unregister from all types
    const typesToUnregister = changeTypes || Object.values(EditorChangeType);

    typesToUnregister.forEach((type) => {
      const observersForType = this.observers.get(type);
      if (observersForType) {
        observersForType.delete(observer);
      }
    });
  }

  /**
   * Queues a notification for later processing
   * @param changeType The type of change that occurred
   * @param payload Optional data associated with the change
   */
  queueNotification(changeType: EditorChangeType, payload?: any): void {
    this.logger.debug(`Queuing notification: ${changeType}`);
    this.pendingNotifications.push({ changeType, payload });
  }

  /**
   * Processes all queued notifications
   * Will only process notifications if not in a batch or sync operation
   */
  processQueuedNotifications(): void {
    // Skip processing if still in a batch or sync operation
    if (this.editor.isInBatch() || this.editor.isSyncingToRenderer()) {
      this.logger.debug('Deferring notification processing (still in batch or sync)');
      return;
    }

    // If there are no notifications, just return
    if (this.pendingNotifications.length === 0) {
      return;
    }

    this.logger.debug(
      `Processing ${this.pendingNotifications.length} queued notifications`,
    );

    // Make a copy of the queue to avoid issues if notifications trigger more notifications
    const notifications = [...this.pendingNotifications];
    this.pendingNotifications = [];

    // Send each notification in order
    notifications.forEach(({ changeType, payload }) => {
      this.sendNotification(changeType, payload);
    });
  }

  /**
   * Sends a notification to all registered observers for a specific change type
   * @param changeType The type of change that occurred
   * @param payload Optional data associated with the change
   */
  private sendNotification(changeType: EditorChangeType, payload?: any): void {
    this.logger.debug(`Sending notification: ${changeType}`);
    const observersForType = this.observers.get(changeType);
    if (observersForType) {
      observersForType.forEach((observer) => {
        observer.onEditorChange(this.editor, changeType, payload);
      });
    }

    // Also trigger the legacy onStateChange handler for backward compatibility
    if (this.editor.onStateChange) {
      this.editor.onStateChange(this.editor.timeline);
    }
  }

  /**
   * Notifies all registered observers about a change
   * If batching or in the middle of a sync operation, queues the notification for later
   * @param changeType The type of change that occurred
   * @param payload Optional data associated with the change
   * @param immediate Optional flag to force immediate notification even during batching (use with caution)
   */
  notifyObservers(
    changeType: EditorChangeType,
    payload?: any,
    immediate: boolean = false,
  ): void {
    // Queue notifications during batching operations or sync operations unless immediate is true
    if ((this.editor.isInBatch() || this.editor.isSyncingToRenderer()) && !immediate) {
      this.queueNotification(changeType, payload);
      return;
    }

    // Otherwise, send the notification immediately
    this.sendNotification(changeType, payload);
  }
}